'use client';

import { CheckCircle2, FileUp, Loader2, Receipt } from 'lucide-react';
import { useEffect, useState } from 'react';
import { UploadAttachButton } from '../file-attach/file-attach';
import { InlineNotice } from '../inline-notice/inline-notice';

export interface BollettaExtractedFields {
	periodoDa?: string;
	periodoA?: string;
	energiaKwh?: number;
	f1Kwh?: number;
	f2Kwh?: number;
	f3Kwh?: number;
	codicePod?: string;
}

export interface BollettaSavedResult {
	days: number;
	from: string;
	to: string;
}

export interface BollettaUploadCardProps {
	/** In corso la lettura del file appena caricato. */
	extracting: boolean;
	/** In corso la scrittura sul misuratore. */
	saving: boolean;
	/** Campi estratti dall'ultimo documento caricato, in attesa di conferma. */
	extracted: BollettaExtractedFields | null;
	confidence: 'high' | 'medium' | 'low' | null;
	/** Esito dell'ultima registrazione confermata. */
	saved: BollettaSavedResult | null;
	error: string | null;
	/** Il file scelto va prima caricato su storage, poi passato qui come URL pubblico. */
	onExtract: (file: File) => void;
	onConfirm: (fields: { periodoDa: string; periodoA: string; energiaKwh: number }) => void;
	onReset: () => void;
}

const CONFIDENCE_LABEL: Record<'high' | 'medium' | 'low', string> = {
	high: 'lettura affidabile',
	medium: 'lettura parziale - controlla i valori',
	low: 'lettura incerta - controlla con attenzione',
};

/**
 * Carica una bolletta vera (PDF), ne legge il testo senza AI e mostra i
 * soli totali che estrae — una bolletta certifica un periodo, non un
 * dettaglio orario, quindi non c'è altro da estrarre. I campi restano
 * modificabili prima di confermare: l'app legge, l'operatore rivede.
 */
export function BollettaUploadCard({
	extracting,
	saving,
	extracted,
	confidence,
	saved,
	error,
	onExtract,
	onConfirm,
	onReset,
}: BollettaUploadCardProps) {
	const [periodoDa, setPeriodoDa] = useState('');
	const [periodoA, setPeriodoA] = useState('');
	const [energiaKwh, setEnergiaKwh] = useState('');
	// La lettura automatica è una comodità, non un requisito: se non è disponibile (o
	// legge male) l'operatore inserisce i totali a mano, negli stessi campi.
	const [manual, setManual] = useState(false);

	useEffect(() => {
		if (!extracted) return;
		setPeriodoDa(extracted.periodoDa ?? '');
		setPeriodoA(extracted.periodoA ?? '');
		const fasce = [extracted.f1Kwh, extracted.f2Kwh, extracted.f3Kwh].filter(
			(v): v is number => typeof v === 'number',
		);
		const totale =
			extracted.energiaKwh ?? (fasce.length > 0 ? fasce.reduce((s, v) => s + v, 0) : undefined);
		setEnergiaKwh(totale != null ? String(totale) : '');
	}, [extracted]);

	const kwhValue = Number(energiaKwh);
	const canConfirm = !!periodoDa && !!periodoA && periodoA >= periodoDa && kwhValue > 0;
	const showForm = (extracted || manual) && !saved;

	function handleFiles(files: FileList | null) {
		const file = files?.[0];
		if (!file) return;
		setManual(false);
		onReset();
		onExtract(file);
	}

	function handleReset() {
		setManual(false);
		onReset();
	}

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">
					<Receipt className="mr-1 inline h-3.5 w-3.5 text-accent-green" />
					Carica bolletta
				</span>
			</div>

			<p className="mb-3 text-[12px] text-text-muted">
				Carica un PDF o una foto, controlla i valori estratti e conferma. Puoi anche inserire i
				totali a mano.
			</p>

			{!showForm && !saved && (
				<div className="flex flex-wrap items-center gap-3">
					<UploadAttachButton
						uploading={extracting}
						onFiles={handleFiles}
						accept=".pdf,image/*"
						multiple={false}
						variant="chip"
						idleLabel="Carica bolletta (PDF o foto)"
						uploadingLabel="Lettura del documento…"
					/>
					<button
						type="button"
						onClick={() => setManual(true)}
						className="text-[12px] text-text-muted underline-offset-2 hover:text-text-primary hover:underline"
					>
						oppure inserisci i dati a mano
					</button>
				</div>
			)}

			{error && (
				<InlineNotice tone="danger" mb="sm">
					{error}
					{!showForm && ' - puoi comunque inserire i dati a mano qui sotto.'}
				</InlineNotice>
			)}

			{showForm && (
				<div className="space-y-3">
					{confidence && (
						<InlineNotice tone={confidence === 'high' ? 'muted' : 'warn'} mb="sm">
							{CONFIDENCE_LABEL[confidence]}
						</InlineNotice>
					)}

					<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
						<label className="text-[12px] text-text-muted">
							Da
							<input
								type="date"
								value={periodoDa}
								onChange={(e) => setPeriodoDa(e.target.value)}
								className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
							/>
						</label>
						<label className="text-[12px] text-text-muted">
							A
							<input
								type="date"
								value={periodoA}
								onChange={(e) => setPeriodoA(e.target.value)}
								className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
							/>
						</label>
						<label className="text-[12px] text-text-muted">
							Totale kWh
							<input
								type="number"
								min="0"
								step="0.01"
								value={energiaKwh}
								onChange={(e) => setEnergiaKwh(e.target.value)}
								className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
							/>
						</label>
					</div>

					{extracted &&
						(extracted.f1Kwh != null || extracted.f2Kwh != null || extracted.f3Kwh != null) && (
							<p className="text-[11px] text-text-muted">
								Fasce lette dal documento:
								{extracted.f1Kwh != null && ` F1 ${extracted.f1Kwh} kWh`}
								{extracted.f2Kwh != null && ` · F2 ${extracted.f2Kwh} kWh`}
								{extracted.f3Kwh != null && ` · F3 ${extracted.f3Kwh} kWh`}
							</p>
						)}

					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							onClick={() => canConfirm && onConfirm({ periodoDa, periodoA, energiaKwh: kwhValue })}
							disabled={saving || !canConfirm}
							className="inline-flex items-center gap-1.5 rounded border border-accent-green/50 bg-accent-green/10 px-3 py-1 text-[13px] text-accent-green transition-colors hover:bg-accent-green/20 disabled:opacity-50"
						>
							{saving ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<FileUp className="h-3 w-3" />
							)}
							{saving ? 'Registrazione…' : 'Conferma e registra'}
						</button>
						<button
							type="button"
							onClick={handleReset}
							disabled={saving}
							className="rounded border border-border px-3 py-1 text-[13px] text-text-muted transition-colors hover:bg-border/20 disabled:opacity-50"
						>
							Annulla
						</button>
					</div>
				</div>
			)}

			{saved && (
				<div className="space-y-2">
					<p className="flex items-center gap-1.5 text-[13px] text-text-primary">
						<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-accent-green" />
						Bolletta registrata · {saved.from} → {saved.to} · {saved.days} giorni scritti
					</p>
					<button
						type="button"
						onClick={handleReset}
						className="rounded border border-border px-3 py-1 text-[13px] text-text-muted transition-colors hover:bg-border/20"
					>
						Carica un'altra bolletta
					</button>
				</div>
			)}
		</div>
	);
}
